"use client";

import React, { forwardRef, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MarkdownEditorCore } from "./markdown-editor-core";
import { MarkdownEditorToolbar } from "./markdown-editor-toolbar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui-kit/tabs";
import { Eye, Pen } from "lucide-react";
import { Textarea } from "@/components/ui-kit/textarea";
import { MarkdownRenderer } from "@/modules/gpt-chats/components/markdown-renderer";

type MarkdownEditorProps = {
  value?: string;
  onChange?: (value: string) => void;
  className?: string;

  error?: string;
  isInvalid?: boolean;

  placeholder?: string;
  disabled?: boolean;
  showToolbar?: boolean;
  showModeToggle?: boolean;
  defaultMode?: "edit" | "preview";

  minLength?: number;
  maxLength?: number;
  showCharacterCount?: boolean;

  onBlur?: (value: string) => void;
  onFocus?: (value: string) => void;
  onModeChange?: (mode: "edit" | "preview") => void;

  toolbarItems?: (keyof EditorCommands)[];

  validate?: (value: string) => string | undefined;
};

export type MarkdownEditorRef = {
  focus: () => void;
  blur: () => void;
  getValue: () => string;
  setValue: (value: string) => void;
  getMode: () => "edit" | "preview";
  setMode: (mode: "edit" | "preview") => void;
  validate: () => string | undefined;
};

export type EditorCommands = {
  h1: () => void;
  h2: () => void;
  h3: () => void;
  bold: () => void;
  italic: () => void;
  unordered: () => void;
  ordered: () => void;
  task: () => void;
  quote: () => void;
  code: () => void;
  link: () => void;
};

export const MarkdownEditor = forwardRef<MarkdownEditorRef, MarkdownEditorProps>(
  ({
    value = "",
    onChange,
    className = "",
    error,
    isInvalid = false,
    placeholder = "Enter your content...",
    disabled = false,
    showToolbar = true,
    showModeToggle = true,
    defaultMode = "edit",
    minLength,
    maxLength,
    showCharacterCount = false,
    onBlur,
    onFocus,
    onModeChange,
    toolbarItems = [
      "h1",
      "h2",
      "h3",
      "bold",
      "italic",
      "unordered",
      "ordered",
      "task",
      "quote",
      "code",
      "link",
    ],
    validate,
  }) => {
    const { t } = useTranslation();
    const [mode, setMode] = useState<"edit" | "preview">(defaultMode);
    const [isFocused, setIsFocused] = useState(false);
    const [internalError, setInternalError] = useState<string>("");

    const coreRef = useRef<MarkdownEditorCore>(new MarkdownEditorCore(value));
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [content, setContent] = useState(value);

    useEffect(() => {
      coreRef.current.setContent(value);
      setContent(value);
    }, [value]);

    const handleModeChange = (newMode: string) => {
      const modeValue = newMode as "edit" | "preview";
      setMode(modeValue);
      onModeChange?.(modeValue);
    };

    const performValidation = (val: string): string => {
      if (validate) {
        const validationResult = validate(val);
        if (validationResult) return validationResult;
      }

      if (minLength && val.length < minLength) {
        return t('MIN_CHARACTERS_REQUIRED', { count: minLength });
      }
      if (maxLength && val.length > maxLength) {
        return t('MAX_CHARACTERS_ALLOWED', { count: maxLength });
      }

      return "";
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (disabled) return;

      const val = e.target.value;
      coreRef.current.setContent(val).setCursor(e.target.selectionStart, e.target.selectionEnd);
      setContent(val);
      onChange?.(val);

      if (internalError) {
        setInternalError("");
      }
    };

    const handleBlur = () => {
      setIsFocused(false);
      const validationError = performValidation(content);
      setInternalError(validationError);
      onBlur?.(content);
    };

    const handleFocus = () => {
      setIsFocused(true);
      onFocus?.(content);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (disabled) return;

      if (e.key === "Enter") {
        e.preventDefault();
        execCommand(coreRef.current.handleEnter);
      }
    };

    const handleSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
      if (disabled) return;
      const el = e.currentTarget;
      coreRef.current.setCursor(el.selectionStart, el.selectionEnd);
    };

    const execCommand = (
      commandFn: () => { content: string; cursor: { start: number; end: number } },
    ) => {
      if (disabled) return;

      const { content, cursor } = commandFn();

      if (maxLength && content.length > maxLength) {
        return;
      }

      setContent(content);
      onChange?.(content);
      const el = textareaRef.current;
      if (!el) return;
      const { start, end } = cursor;
      requestAnimationFrame(() => {
        el.selectionStart = start;
        el.selectionEnd = end;
        el.focus();
      });
    };

    const commands = {
      h1: () => execCommand(coreRef.current.h1),
      h2: () => execCommand(coreRef.current.h2),
      h3: () => execCommand(coreRef.current.h3),
      bold: () => execCommand(coreRef.current.bold),
      italic: () => execCommand(coreRef.current.italic),
      unordered: () => execCommand(coreRef.current.unordered),
      ordered: () => execCommand(coreRef.current.ordered),
      task: () => execCommand(coreRef.current.task),
      quote: () => execCommand(coreRef.current.quote),
      code: () => execCommand(coreRef.current.code),
      link: () => execCommand(coreRef.current.link),
    };

    const filteredCommands = Object.fromEntries(
      Object.entries(commands).filter(([key]) =>
        toolbarItems.includes(key as keyof EditorCommands),
      ),
    ) as Partial<EditorCommands>;

    const displayError = error || internalError;
    const hasError = isInvalid || !!displayError;
    const isUnderMinLength = minLength ? content.length < minLength : false;

    const characterCount = content.length;

    return (
      <div className="flex h-full w-full flex-col gap-3">
        {(showToolbar || showModeToggle) && (
          <div className="flex flex-wrap items-center gap-2">
            {showToolbar && (
              <MarkdownEditorToolbar
                commands={filteredCommands as EditorCommands}
                disabled={disabled || mode === "preview"}
              />
            )}
            {showModeToggle && (
              <Tabs
                value={mode}
                onValueChange={handleModeChange}
                className="max-h-8 md:flex-1 md:text-right"
              >
                <TabsList className="max-h-8 rounded-sm p-1">
                  <TabsTrigger value="edit" className="h-6">
                    <Pen className="aspect-square w-4" />
                  </TabsTrigger>
                  <TabsTrigger value="preview" className="h-6">
                    <Eye className="aspect-square w-4" />
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            )}
          </div>
        )}

        {mode === "edit" ? (
          <Textarea
            ref={textareaRef}
            className={`w-full rounded border p-4 leading-relaxed ${className} ${hasError || isUnderMinLength ? "border-error focus:border-error" : ""} ${isFocused ? "ring-2 ring-blue-500 ring-opacity-20" : ""} ${disabled ? "cursor-not-allowed opacity-50" : ""} `}
            value={content}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onSelect={handleSelect}
            onBlur={handleBlur}
            onFocus={handleFocus}
            placeholder={placeholder || t('ENTER_YOUR_CONTENT')}
            disabled={disabled}
            maxLength={maxLength}
          />
        ) : (
          <MarkdownRenderer
            content={content || placeholder}
            className={`w-full overflow-y-auto rounded border p-4 ${className} ${content === "" ? "italic text-gray-400" : ""} `}
          />
        )}

        {(showCharacterCount || displayError) && (
          <div className="flex items-start justify-between gap-2 text-sm">
            {showCharacterCount && (
              <div className={isUnderMinLength ? "text-error" : "text-gray-500"}>
                {characterCount}
                {maxLength && ` / ${maxLength}`}
              </div>
            )}
            {/* {displayError && <div className="flex-1 text-right text-error">{displayError}</div>} */}
          </div>
        )}
      </div>
    );
  },
);

MarkdownEditor.displayName = "MarkdownEditor";
